(() => {
  if (window.__KP_PRODUCT_MEDIA_V2__) return;
  window.__KP_PRODUCT_MEDIA_V2__ = true;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const MAX_FEATURED_VIDEOS = 3;
  const initialized = new WeakSet();

  const isVideoSlide = (slide) => {
    const deferred = slide.querySelector('deferred-media');
    if (!deferred) return false;

    const template = deferred.querySelector('template');
    if (!template) return false;

    return Boolean(template.content?.querySelector('video, iframe'));
  };

  const getPlayable = (item) => item.querySelector('video, iframe');

  const prepareVideo = (video) => {
    if (!video || video.tagName !== 'VIDEO') return;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');
  };

  const pauseStripVideos = (strip, except = null) => {
    strip.querySelectorAll('video').forEach((video) => {
      if (video !== except) video.pause();
    });
  };

  const loadAndPlay = async (item, strip) => {
    const deferred = item.querySelector('deferred-media');
    if (!deferred) return;

    if (!deferred.getAttribute('loaded') && typeof deferred.loadContent === 'function') {
      deferred.loadContent(false);
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    const playable = getPlayable(item);
    if (!playable) return;

    if (playable.tagName === 'VIDEO') {
      prepareVideo(playable);
      pauseStripVideos(strip, playable);
      try {
        await playable.play();
      } catch (_) {
        // Browser autoplay policy can still refuse playback; poster remains usable.
      }
    }
  };

  const pauseItem = (item) => {
    const video = item.querySelector('video');
    if (video) video.pause();
  };

  const createRail = (gallery, slides) => {
    if (slides.length <= 1) return null;

    // IMPORTANT: keep this custom rail out of <ul>. Dawn's ProductInfo.updateMedia()
    // deliberately queries the first "media-gallery ul" as the canonical media list.
    // If our custom thumbnail rail is also a <ul> and sits before GalleryViewer, Dawn
    // mutates the thumbnails instead of the real gallery when a colour variant changes.
    const rail = document.createElement('div');
    rail.className = 'kp-product-image-rail';
    rail.dataset.kpPrimaryRail = 'true';
    rail.setAttribute('role', 'list');
    rail.setAttribute('aria-label', 'Product media');

    slides.forEach((slide, index) => {
      const mediaId = slide.dataset.mediaId;
      const sourceImage = slide.querySelector('img');
      if (!mediaId || !sourceImage) return;

      const li = document.createElement('div');
      li.className = 'kp-product-image-rail__item';
      li.setAttribute('role', 'listitem');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kp-product-image-rail__button';
      button.dataset.target = mediaId;
      button.setAttribute('aria-label', `View product media ${index + 1}`);
      button.setAttribute('aria-current', slide.classList.contains('is-active') ? 'true' : 'false');

      const img = sourceImage.cloneNode(true);
      img.removeAttribute('id');
      img.removeAttribute('class');
      img.loading = 'lazy';
      img.sizes = '76px';
      img.alt = sourceImage.alt || '';

      button.appendChild(img);
      li.appendChild(button);
      rail.appendChild(li);

      button.addEventListener('click', () => {
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(mediaId, false);
        } else {
          slides.forEach((candidate) => candidate.classList.toggle('is-active', candidate === slide));
        }
        syncRail(gallery, rail);
      });
    });

    return rail.children.length > 1 ? rail : null;
  };

  const syncRail = (gallery, rail) => {
    if (!rail) return;
    const active = gallery.querySelector("[id^='Slider-Gallery-'] > .product__media-item.is-active");
    const activeId = active?.dataset.mediaId;

    rail.querySelectorAll('.kp-product-image-rail__button').forEach((button) => {
      button.setAttribute('aria-current', button.dataset.target === activeId ? 'true' : 'false');
    });
  };

  const replaceRail = (gallery, viewer, slides) => {
    gallery.querySelectorAll(':scope > .kp-product-image-rail').forEach((candidate) => candidate.remove());
    const nextRail = createRail(gallery, slides);
    if (nextRail) gallery.insertBefore(nextRail, viewer);
    return nextRail;
  };

  const getSelectedVariantMediaId = (gallery) => {
    const productInfo = gallery.closest('product-info');
    const variantNode = productInfo?.querySelector('variant-selects [data-selected-variant]');
    if (!productInfo || !variantNode) return '';

    try {
      const variant = JSON.parse(variantNode.textContent || '{}');
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return '';
      return `${productInfo.dataset.section}-${mediaId}`;
    } catch (_) {
      return '';
    }
  };

  const syncSelectedVariantMedia = (gallery, list, rail) => {
    const targetId = getSelectedVariantMediaId(gallery);
    if (targetId) {
      const target = list.querySelector(`[data-media-id="${CSS.escape(targetId)}"]`);
      if (target && !target.classList.contains('is-active') && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(targetId, true);
      }
    }
    syncRail(gallery, rail);
  };

  const attachDesktopPlayback = (strip) => {
    strip.querySelectorAll('.kp-product-video').forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (window.matchMedia(DESKTOP_QUERY).matches) loadAndPlay(item, strip);
      });
      item.addEventListener('mouseleave', () => {
        if (window.matchMedia(DESKTOP_QUERY).matches) pauseItem(item);
      });
      item.addEventListener('focusin', () => {
        if (window.matchMedia(DESKTOP_QUERY).matches) loadAndPlay(item, strip);
      });
      item.addEventListener('focusout', () => {
        if (window.matchMedia(DESKTOP_QUERY).matches) pauseItem(item);
      });
    });
  };

  const attachMobilePlayback = (strip) => {
    if (!('IntersectionObserver' in window)) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const observer = new IntersectionObserver(
      (entries) => {
        if (window.matchMedia(DESKTOP_QUERY).matches || reduceMotion) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.62)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible) loadAndPlay(visible.target, strip);
        entries
          .filter((entry) => entry.target !== visible?.target)
          .forEach((entry) => pauseItem(entry.target));
      },
      { threshold: [0, 0.35, 0.62, 0.85] },
    );

    strip.querySelectorAll('.kp-product-video').forEach((item) => observer.observe(item));
  };

  const initGallery = (gallery) => {
    if (!gallery || initialized.has(gallery) || gallery.dataset.kpProductMediaInitialized === 'true') return;
    gallery.dataset.kpProductMediaInitialized = 'true';

    const viewer = gallery.querySelector("slider-component[id^='GalleryViewer-']");
    const list = viewer?.querySelector("[id^='Slider-Gallery-']");
    if (!viewer || !list) return;

    const originalSlides = Array.from(list.querySelectorAll(':scope > .product__media-item[data-media-id]'));
    if (!originalSlides.length) return;

    const videoSlides = originalSlides.filter(isVideoSlide);
    const featuredVideos = videoSlides.slice(0, MAX_FEATURED_VIDEOS);

    let strip = null;
    if (featuredVideos.length) {
      strip = document.createElement('ul');
      strip.className = 'kp-product-video-strip';
      strip.style.setProperty('--kp-video-count', String(featuredVideos.length));
      strip.dataset.count = String(featuredVideos.length);
      strip.setAttribute('aria-label', 'Product videos');

      featuredVideos.forEach((slide) => {
        const mediaId = slide.dataset.mediaId;
        slide.classList.remove('grid__item', 'slider__slide', 'is-active', 'product__media-item--full');
        slide.classList.add('kp-product-video');
        strip.appendChild(slide);

        gallery.querySelectorAll(`[data-target="${CSS.escape(mediaId)}"]`).forEach((thumbnail) => thumbnail.remove());
      });

      viewer.insertAdjacentElement('afterend', strip);
      attachDesktopPlayback(strip);
      attachMobilePlayback(strip);
    }

    const remainingSlides = Array.from(list.querySelectorAll(':scope > .product__media-item[data-media-id]'));
    if (!remainingSlides.length) return;

    if (!remainingSlides.some((slide) => slide.classList.contains('is-active'))) {
      remainingSlides[0].classList.add('is-active');
    }

    let rail = replaceRail(gallery, viewer, remainingSlides);

    if (typeof viewer.resetPages === 'function') viewer.resetPages();

    let mutationQueued = false;
    const observer = new MutationObserver((mutations) => {
      if (mutationQueued) return;
      mutationQueued = true;
      requestAnimationFrame(() => {
        mutationQueued = false;
        const hasListChange = mutations.some((mutation) => mutation.type === 'childList');
        if (hasListChange) {
          const currentSlides = Array.from(
            list.querySelectorAll(':scope > .product__media-item[data-media-id]')
          );
          rail = replaceRail(gallery, viewer, currentSlides);
          if (typeof viewer.resetPages === 'function') viewer.resetPages();
        }
        syncSelectedVariantMedia(gallery, list, rail);
      });
    });
    observer.observe(list, {
      subtree: false,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    const productInfo = gallery.closest('product-info');
    productInfo?.addEventListener('change', (event) => {
      if (!event.target?.matches?.('variant-selects input, variant-selects select, product-form input[name="id"]')) return;
      [0, 120, 350, 800].forEach((delay) => {
        window.setTimeout(() => syncSelectedVariantMedia(gallery, list, rail), delay);
      });
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = event?.data?.sectionId;
        if (sectionId && productInfo?.dataset?.section && sectionId !== productInfo.dataset.section) return;
        window.setTimeout(() => {
          const currentSlides = Array.from(
            list.querySelectorAll(':scope > .product__media-item[data-media-id]')
          );
          rail = replaceRail(gallery, viewer, currentSlides);
          syncSelectedVariantMedia(gallery, list, rail);
        }, 0);
      });
    }

    gallery.classList.add('kp-product-media-ready');
    initialized.add(gallery);
    syncSelectedVariantMedia(gallery, list, rail);
  };

  const initAll = (root = document) => {
    root.querySelectorAll?.('media-gallery').forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
